import { create } from 'zustand';
import roleFeatureService from '../services/roleFeatureService';
import userFeatureFlagService from '../services/userFeatureFlagService';
import { FeatureDefinition, FeatureFlagState } from '../types/roleFeature';
import { Role } from '../types/user';
import useAuthStore from './authStore';

// ============================================================================
// DEPRECATED: This feature flag store is legacy code and should not be used
// ============================================================================
// 
// This file contains old hardcoded feature definitions that are no longer maintained.
// All feature management has been moved to the Supabase 'features' table.
// 
// For new features:
// 1. Add them to the 'features' table in Supabase
// 2. Use the featureService.ts to interact with features
// 3. Features are now managed through the admin UI and database
// 
// This file should be removed once all references are migrated to the new system.
// ============================================================================

// Default features that match navigation items
export const availableFeatures: FeatureDefinition[] = [
  { 
    key: 'dashboard', 
    name: 'Dashboard', 
    description: 'Main dashboard page',
    defaultEnabledForRoles: ['admin', 'user']
  },
  {
    key: 'google_reviews_ops',
    name: 'Google Reviews Ops',
    description: 'Operational workflows for Google Reviews scrape and analysis',
    defaultEnabledForRoles: ['admin', 'user']
  },
  {
    key: 'google_reviews_dashboard',
    name: 'Google Reviews Dashboard',
    description: 'Google Reviews KPI dashboard page',
    defaultEnabledForRoles: ['admin', 'user']
  },
  { 
    key: 'upload_csv', 
    name: 'Upload CSV', 
    description: 'Upload CSV files',
    defaultEnabledForRoles: ['admin', 'user']
  },
  { 
    key: 'upload_csv_questions', 
    name: 'Upload CSV with Questions', 
    description: 'Upload CSV files with questionnaire',
    defaultEnabledForRoles: ['admin', 'user']
  },
  { 
    key: 'csv_demo', 
    name: 'CSV Demo', 
    description: 'CSV demonstration page',
    defaultEnabledForRoles: ['admin', 'user']
  },
  { 
    key: 'brand_maintenance', 
    name: 'Brand Maintenance', 
    description: 'Manage brands',
    defaultEnabledForRoles: ['admin', 'user']
  },
  { 
    key: 'market_maintenance', 
    name: 'Market Maintenance', 
    description: 'Manage markets',
    defaultEnabledForRoles: ['admin', 'user']
  },
  { 
    key: 'dealer_group_maintenance', 
    name: 'Dealer Group Maintenance', 
    description: 'Manage dealer groups',
    defaultEnabledForRoles: ['admin', 'user']
  },
  { 
    key: 'kpi_maintenance', 
    name: 'KPI Maintenance', 
    description: 'Manage Key Performance Indicators',
    defaultEnabledForRoles: ['admin', 'user']
  },
  { 
    key: 'generate_ai_validations', 
    name: 'Generate AI Validations', 
    description: 'Generate AI-powered validation rules',
    defaultEnabledForRoles: ['admin', 'user']
  },
  { 
    key: 'user_questions', 
    name: 'User Questions', 
    description: 'Manage User Questions',
    defaultEnabledForRoles: ['admin', 'user']
  },
  { 
    key: 'map_data', 
    name: 'Map Data', 
    description: 'Data mapping functionality',
    defaultEnabledForRoles: ['admin', 'user']
  },
  { 
    key: 'financial_data', 
    name: 'View Data', 
    description: 'View financial data',
    defaultEnabledForRoles: ['admin', 'user']
  },
  { 
    key: 'analytics', 
    name: 'Analytics', 
    description: 'Analytics dashboard',
    defaultEnabledForRoles: ['admin', 'user']
  },
  { 
    key: 'kpi_analytics', 
    name: 'KPI Analytics', 
    description: 'KPI Analytics dashboard',
    defaultEnabledForRoles: ['admin', 'user']
  },
  { 
    key: 'visuai_data_viewer', 
    name: 'VisuAI Data Viewer', 
    description: 'AI-powered data visualization',
    defaultEnabledForRoles: ['admin', 'user']
  },
  { 
    key: 'user_maintenance', 
    name: 'User Maintenance', 
    description: 'Manage users',
    defaultEnabledForRoles: ['admin']
  },
  { 
    key: 'group_user_maintenance', 
    name: 'Group User Management', 
    description: 'Manage group users',
    defaultEnabledForRoles: ['admin', 'group_admin']
  },
  { 
    key: 'role_maintenance', 
    name: 'Role Maintenance', 
    description: 'Manage role permissions',
    defaultEnabledForRoles: ['admin']
  },
  { 
    key: 'super_admin', 
    name: 'Super Admin', 
    description: 'Super Admin organization management',
    defaultEnabledForRoles: []
  },
  { 
    key: 'location_management', 
    name: 'Location Management', 
    description: 'Manage locations across organizations',
    defaultEnabledForRoles: ['admin']
  },
  { 
    key: 'hierarchy_management', 
    name: 'Hierarchy Management', 
    description: 'Manage custom hierarchy levels and nodes',
    defaultEnabledForRoles: ['admin']
  },
  { 
    key: 'user_access_management', 
    name: 'User Access Management', 
    description: 'Assign users to organizations, hierarchy nodes, or locations',
    defaultEnabledForRoles: ['admin']
  },
  { 
    key: 'organisation_kpis', 
    name: 'Organisation KPIs', 
    description: 'Manage organization-level KPI categories and definitions',
    defaultEnabledForRoles: ['admin']
  },
  { 
    key: 'submit_data', 
    name: 'Submit Data', 
    description: 'Submit financial data for processing',
    defaultEnabledForRoles: ['admin', 'user']
  },
  { 
    key: 'submission_quality_check', 
    name: 'Submission Quality Check', 
    description: 'Review and analyze quality of submitted financial data',
    defaultEnabledForRoles: ['admin', 'user']
  }
];

const useFeatureFlagStore = create<FeatureFlagState>((set, get) => ({
  featureFlags: {} as Record<string, Record<string, boolean>>,
  currentRoleFlags: {},
  currentRole: null,
  // For user feature role permissions
  userFeaturePermissions: {},
  userPermissionsLoaded: false,
  // For role maintenance page
  managedRoleFlags: {},
  managedRole: null,
  isLoading: false,
  error: null,
  availableFeatures,
  
  // Initialize with user from auth store
  initializeWithUser: () => {
    const { user } = useAuthStore.getState();
    if (user && user.id) {
      // Fetch user's feature role permissions instead of traditional role permissions
      get().fetchUserFeaturePermissions(String(user.id));
    }
  },
  
  // Fetch feature flags for the current user's role
  fetchFeatureFlags: async (roleId: string) => {
    // Prevent duplicate calls - check if already loading or if we already have flags for this role
    const { isLoading, featureFlags } = get();
    if (isLoading || featureFlags[roleId]) {
      return;
    }


    set({ isLoading: true });
    try {
      const flags = await roleFeatureService.getRoleFeatures(roleId);
      const flagMap = flags.reduce((acc, flag) => {
        acc[flag.featureKey] = flag.enabled;
        return acc;
      }, {} as Record<string, boolean>);
      

      set(state => ({ 
        featureFlags: {
          ...state.featureFlags,
          [roleId]: flagMap
        },
        currentRoleFlags: flagMap,
        currentRole: roleId,
        isLoading: false, 
        error: null 
      }));
      
    } catch (error: any) {
      console.error(`❌ Failed to fetch feature flags for role ${roleId}:`, error);
      
      // Generate default feature flags based on available features
      const defaultFlags = get().availableFeatures.reduce((acc, feature) => {
        // Enable features based on default settings for this role
        acc[feature.key] = feature.defaultEnabledForRoles.includes(roleId as any);
        return acc;
      }, {} as Record<string, boolean>);
      

      // Set the default flags
      set(state => ({ 
        featureFlags: {
          ...state.featureFlags,
          [roleId]: defaultFlags
        },
        currentRoleFlags: defaultFlags,
        currentRole: roleId,
        isLoading: false, 
        error: `Could not load feature flags from server: ${error.message || 'Unknown error'}. Using default values.`
      }));
    }
  },

  // Fetch user's feature permissions based on assigned feature roles
  fetchUserFeaturePermissions: async (userId: string) => {
    const { isLoading, userPermissionsLoaded } = get();
    
    // Prevent duplicate calls - check if already loading or already loaded for this user
    if (isLoading || userPermissionsLoaded) {
      return;
    }


    set({ isLoading: true });
    try {
      const permissions = await userFeatureFlagService.getUserFeaturePermissions(userId);

      
      set(state => ({ 
        userFeaturePermissions: permissions,
        userPermissionsLoaded: true,
        isLoading: false, 
        error: null 
      }));
    } catch (error: any) {
      console.error(`❌ Error loading user feature permissions:`, error);
      
      // Set empty permissions on error
      set(state => ({ 
        userFeaturePermissions: {},
        userPermissionsLoaded: true,
        isLoading: false, 
        error: `Could not load user feature permissions: ${error.message || 'Unknown error'}`
      }));
    }
  },
  
  // Fetch feature flags for role management (admin only)
  fetchRoleFeatureFlags: async (roleId: string) => {
    set({ isLoading: true });
    try {
      const flags = await roleFeatureService.getRoleFeatures(roleId);
      const flagMap = flags.reduce((acc, flag) => {
        acc[flag.featureKey] = flag.enabled;
        return acc;
      }, {} as Record<string, boolean>);
      
      set(state => ({ 
        featureFlags: {
          ...state.featureFlags,
          [roleId]: flagMap
        },
        managedRoleFlags: flagMap,
        managedRole: roleId,
        isLoading: false, 
        error: null 
      }));
    } catch (error: any) {
      console.error(`Failed to fetch feature flags for managed role ${roleId}:`, error);
      
      // Generate default feature flags based on available features
      const defaultFlags = get().availableFeatures.reduce((acc, feature) => {
        // Enable features based on default settings for this role
        acc[feature.key] = feature.defaultEnabledForRoles.includes(roleId as any);
        return acc;
      }, {} as Record<string, boolean>);
      
      // Set the default flags
      set(state => ({ 
        featureFlags: {
          ...state.featureFlags,
          [roleId]: defaultFlags
        },
        managedRoleFlags: defaultFlags,
        managedRole: roleId,
        isLoading: false, 
        error: `Could not load feature flags from server: ${error.message || 'Unknown error'}. Using default values.`
      }));
    }
  },
  
  updateFeatureFlag: async (roleId: string, featureKey: string, enabled: boolean) => {
    try {
      await roleFeatureService.updateFeatureFlag(roleId, featureKey, enabled);
      
      // Update local state by role
      set(state => {
        // Create a partial state update object with proper typing
        const newState: Partial<FeatureFlagState> = {
          featureFlags: {
            ...state.featureFlags,
            [roleId]: {
              ...(state.featureFlags[roleId] || {}),
              [featureKey]: enabled
            }
          }
        };
        
        // If updating the current user's role, also update currentRoleFlags
        if (roleId === state.currentRole) {
          newState.currentRoleFlags = {
            ...state.currentRoleFlags,
            [featureKey]: enabled
          };
        }
        
        // If updating the managed role, also update managedRoleFlags
        if (roleId === state.managedRole) {
          newState.managedRoleFlags = {
            ...state.managedRoleFlags,
            [featureKey]: enabled
          };
        }
        
        return newState;
      });
    } catch (error: any) {
      console.error(`Failed to update feature flag ${featureKey} for role ${roleId}:`, error);
      
      // Even though the API call failed, still update the local state
      // This allows the UI to work in development mode or when the backend is unavailable
      
      set(state => {
        // Create a partial state update object with proper typing
        const newState: Partial<FeatureFlagState> = {
          featureFlags: {
            ...state.featureFlags,
            [roleId]: {
              ...(state.featureFlags[roleId] || {}),
              [featureKey]: enabled
            }
          },
          error: `Could not save changes to server: ${error.message || 'Unknown error'}. Changes are temporary.`
        };
        
        // If updating the current user's role, also update currentRoleFlags
        if (roleId === state.currentRole) {
          newState.currentRoleFlags = {
            ...state.currentRoleFlags,
            [featureKey]: enabled
          };
        }
        
        // If updating the managed role, also update managedRoleFlags
        if (roleId === state.managedRole) {
          newState.managedRoleFlags = {
            ...state.managedRoleFlags,
            [featureKey]: enabled
          };
        }
        
        return newState;
      });
    }
  },
  
  isFeatureEnabled: (featureKey: string) => {
    const { userFeaturePermissions, userPermissionsLoaded } = get();
    
    // Always get the latest user information from auth store
    const authUser = useAuthStore.getState().user;
    
    // Removed verbose feature flag logging to clean up console
    
    // Special case for super admins - they always have access to everything
    if (authUser?.is_super_admin) {
      return true;
    }
    
    // Special case for admins without organization - they have access to everything
    if (authUser?.role === 'admin' && !authUser?.organization_id) {
      return true;
    }
    
    // If user feature permissions are loaded, use those
    if (userPermissionsLoaded) {
      // Check if user has permission for this feature
      const hasPermission = userFeaturePermissions[featureKey] === true;
      
      // Always allow dashboard access
      if (featureKey === 'dashboard') {
        return true;
      }
      

      return hasPermission;
    }
    
    // If permissions not loaded yet, default to dashboard only
    const allowed = featureKey === 'dashboard';
    return allowed;
  },
  
  isManagedFeatureEnabled: (featureKey: string) => {
    const { managedRoleFlags, managedRole } = get();
    
    // If managedRole is not set yet, return false
    if (!managedRole) {
      return false;
    }
    
    // If the feature exists in managedRoleFlags, return its value
    if (featureKey in managedRoleFlags) {
      return managedRoleFlags[featureKey] === true;
    }
    
    // Default to false for safety
    return false;
  }
}));

// Subscribe to auth store changes to initialize feature permissions
let lastInitializedUserId: string | null = null;
useAuthStore.subscribe((state) => {
  if (state.user && state.isAuthenticated && state.user.id) {
    const currentUserId = String(state.user.id);
    // Only initialize if the user has changed
    if (lastInitializedUserId !== currentUserId) {
      lastInitializedUserId = currentUserId;
      useFeatureFlagStore.getState().initializeWithUser();
    }
  } else {
    // Reset when user logs out
    lastInitializedUserId = null;
    useFeatureFlagStore.setState({
      userFeaturePermissions: {},
      userPermissionsLoaded: false
    });
  }
});

export default useFeatureFlagStore; 